/**
 * Chess-specific formatting utilities shared across features.
 */

/** Formats move clock time as "M:SS" — e.g. "1:24", "0:03" */
export const formatTimeSpent = (seconds: number): string => {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

/**
 * Converts a Chess.com opening URL to a readable name.
 * e.g. "https://www.chess.com/openings/London-System" → "London System"
 * Strips trailing variation details like "-4...Bg7".
 */
export const ecoName = (url: string): string => {
  try {
    const slug = new URL(url).pathname.split("/").pop() ?? url;
    return conciseOpeningName(decodeURIComponent(slug));
  } catch {
    return conciseOpeningName(url);
  }
};

/** Removes Chess.com's move-line suffixes and normalizes opening slugs for display. */
export const friendlyOpeningName = (name: string): string =>
  name
    .replace(/-\d+\..*$/, "")
    .replace(/\s+\d+\..*$/, "")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Uses an explicitly structured variation when the source provides one.
 * Never guesses hierarchy from flattened words.
 */
export const conciseOpeningName = (name: string): string => {
  const friendly = friendlyOpeningName(name);
  return (
    friendly
      .split(/\s*:\s*/)
      .filter(Boolean)
      .at(-1) ?? friendly
  );
};

/** Win/loss/draw color for result display. */
export const resultColor = (result: string): string => {
  if (result === "win") return "#22c55e";
  if (result === "loss") return "#ef4444";
  return "#6b7280";
};
